import { mayOpen } from './menus';
import type { MenuKey } from './menus';
import { DEFAULT_LAYOUT, sanitiseLayout } from './rails';
import type { NavLayout, NavRail } from './rails';
import type { User, Well } from './types';

/**
 * Who may do what, and where.
 *
 * Three things the superadmin sets, and how they join up:
 *
 *   Role        a name, a list of permissions, a well scope and one layout.
 *   Layout      the sections and tabs a person with that role sees.
 *   Assignment  a user holds one role inside one company, on the wells picked
 *               for them.
 *
 * Everything in this file is a pure function of that data, so the pages, the
 * portal and the tests all reach the same answer.
 */

// ------------------------------------------------------------- permissions

interface PermissionMeta {
  key: string;
  group: 'Portal' | 'Well data' | 'Data requests' | 'Administration';
  label: string;
  /** The portal menu this permission opens, when it is one. */
  menu?: MenuKey;
  /** False while no screen acts on it yet: it can be granted, nothing checks it. */
  enforced: boolean;
}

export const PERMISSIONS = [
  { key: 'overview.view', group: 'Portal', label: 'Open Overview', menu: 'overview', enforced: true },
  { key: 'rtlogs.view', group: 'Portal', label: 'Open RT Logs', menu: 'rt-logs', enforced: true },
  { key: 'documents.view', group: 'Portal', label: 'Open Documents', menu: 'documents', enforced: true },
  { key: 'ai.use', group: 'Portal', label: 'Use Ask AI', menu: 'ai', enforced: true },
  { key: 'knowledge.view', group: 'Portal', label: 'Open the Knowledge Base', menu: 'knowledge', enforced: true },
  {
    key: 'documents.manage',
    group: 'Well data',
    label: 'Upload, rename and delete documents',
    enforced: true,
  },
  {
    key: 'logs.configure',
    group: 'Well data',
    label: 'Choose the curves, colours and properties of the logs',
    enforced: false,
  },
  { key: 'data.request', group: 'Data requests', label: 'Ask Data Custody for data', enforced: false },
  {
    key: 'data.fulfil',
    group: 'Data requests',
    label: 'Receive data requests, group the data and send it by email',
    enforced: false,
  },
  {
    key: 'users.manage',
    group: 'Administration',
    label: 'Create and manage users on their own wells',
    enforced: true,
  },
  {
    key: 'welladmins.manage',
    group: 'Administration',
    label: 'Create Well Admins and assign them to wells',
    enforced: true,
  },
] as const satisfies readonly PermissionMeta[];

export type PermissionKey = (typeof PERMISSIONS)[number]['key'];

export const PERMISSION_GROUPS = ['Portal', 'Well data', 'Data requests', 'Administration'] as const;

export function isPermissionKey(value: unknown): value is PermissionKey {
  return PERMISSIONS.some((p) => p.key === value);
}

const MENU_PERMISSION: Record<MenuKey, PermissionKey> = {
  overview: 'overview.view',
  'rt-logs': 'rtlogs.view',
  documents: 'documents.view',
  ai: 'ai.use',
  knowledge: 'knowledge.view',
};

// ------------------------------------------------------------------ records

/**
 * How many wells a role reaches.
 *
 *   none  no wells at all — a Company Admin manages people, not data.
 *   one   exactly one well, picked on the user.
 *   many  one or more wells, picked on the user.
 *   all   every well of the user's company, including ones added later.
 */
export type WellScope = 'none' | 'one' | 'many' | 'all';

export const WELL_SCOPES: { key: WellScope; label: string; hint: string }[] = [
  { key: 'none', label: 'No wells', hint: 'Manages people only; opens no well.' },
  { key: 'one', label: 'One well', hint: 'Exactly one well, chosen on the user.' },
  { key: 'many', label: 'Selected wells', hint: 'One or more wells, chosen on the user.' },
  { key: 'all', label: 'All wells of the company', hint: 'Every well of the company, new ones included.' },
];

/**
 * Where a company stands. Only an active one opens anything for its people;
 * inactive and suspended both switch it off and differ in what they say:
 * inactive is a company not in use, suspended is one stopped on purpose.
 */
export type CompanyStatus = 'active' | 'inactive' | 'suspended';

export const COMPANY_STATUSES: { key: CompanyStatus; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'suspended', label: 'Suspended' },
];

export interface Company {
  id: number;
  /** The name shown in the portal. */
  name: string;
  /** The registered name, e.g. "Petroliam Nasional Berhad". Optional. */
  legalName?: string;
  /** Short form for tables and tags, e.g. "EXE". */
  code: string;
  /** Always `status === 'active'`; kept because every access check reads it. */
  active: boolean;
  /** Absent on a company saved before statuses existed; see companyStatus. */
  status?: CompanyStatus;
  createdAt: string;
}

/** A company's status, whenever it was saved. */
export function companyStatus(company: Company): CompanyStatus {
  if (company.status === 'active' || company.status === 'inactive' || company.status === 'suspended') {
    // The switch wins if the two ever disagree: it is what access is decided on.
    if ((company.status === 'active') === company.active) return company.status;
  }
  return company.active ? 'active' : 'inactive';
}

/**
 * One supplier of data for a well. The same well is measured by several
 * service companies; each feed carries a level (1, 2, 3 or higher), and the order of
 * the list is how far each is trusted, most trusted first. The two are kept
 * apart on purpose: a level 1 real-time feed can be moved to the top.
 */
export interface DataSource {
  id: number;
  company: string;
  level: number;
  realtime: boolean;
}

/** What the admin area adds to a well the API already knows. */
export interface WellExtra {
  companyId: number | null;
  sources: DataSource[];
}

export interface AdminWell extends Well {
  companyId: number | null;
  sources: DataSource[];
  /** Created in the admin area and held in this browser; the API has no row for it. */
  local: boolean;
}

export interface NamedLayout extends NavLayout {
  id: number;
  name: string;
  rails: NavRail[];
}

export interface Role {
  id: number;
  name: string;
  description: string;
  scope: WellScope;
  permissions: PermissionKey[];
  layoutId: number;
}

export interface AdminUser {
  id: number;
  name: string;
  email: string;
  companyId: number;
  roleId: number;
  /** Meaningful for scope "one" and "many"; empty otherwise. */
  wellIds: number[];
  active: boolean;
  createdAt: string;
  createdBy: string;
}

export interface AdminStore {
  version: 1;
  seq: number;
  companies: Company[];
  /** Wells created here. Wells the API serves are not copied in. */
  wells: Well[];
  wellExtras: Record<string, WellExtra>;
  layouts: NamedLayout[];
  roles: Role[];
  users: AdminUser[];
  /**
   * The wells the API served when this was loaded. With a real API every well
   * is here and `wells` above is empty; in mock mode these are the mock's
   * wells and `wells` holds the ones created in the browser.
   */
  serverWells?: Well[];
}

/** The layout every role starts on, and the one that cannot be deleted. */
export const STANDARD_LAYOUT_ID = 1;

export const NAME_MAX = 80;

// ------------------------------------------------------------------- wells

const NO_EXTRA: WellExtra = { companyId: null, sources: [] };

/** The API's wells and the ones created here, as one list with their company. */
export function mergeWells(apiWells: Well[], store: AdminStore): AdminWell[] {
  const extra = (id: number) => store.wellExtras[String(id)] ?? NO_EXTRA;
  const localIds = new Set(store.wells.map((w) => w.id));

  return [
    ...apiWells.filter((w) => !localIds.has(w.id)).map((w) => ({ ...w, ...extra(w.id), local: false })),
    ...store.wells.map((w) => ({ ...w, ...extra(w.id), local: true })),
  ];
}

// ------------------------------------------------------------------ access

export interface Access {
  /**
   *   superadmin  the API's superadmin: everything, everywhere.
   *   role        a user of the admin area: what their role says.
   *   legacy      an account the admin area does not know: as before it existed.
   */
  kind: 'superadmin' | 'role' | 'legacy';
  /** True while a superadmin is looking at the portal as somebody else. */
  preview: boolean;
  roleName: string;
  subject: AdminUser | null;
  role: Role | null;
  companyId: number | null;
  can: (permission: PermissionKey) => boolean;
  canOpen: (menu: MenuKey) => boolean;
  seesWell: (well: AdminWell) => boolean;
  layout: NavLayout;
  /** Which parts of the admin area this person may open. */
  admin: { structure: boolean; users: boolean };
}

function layoutOf(store: AdminStore, id: number): NavLayout {
  const found =
    store.layouts.find((l) => l.id === id) ?? store.layouts.find((l) => l.id === STANDARD_LAYOUT_ID);
  return found ? { rails: found.rails } : DEFAULT_LAYOUT;
}

/**
 * What the person in front of the screen may do.
 *
 * `previewUserId` is honoured only for a real superadmin: it swaps in another
 * user's access so the superadmin can see what they set up, and nothing more —
 * the session, and what the API would allow, stay the superadmin's.
 */
export function resolveAccess(
  user: User | null,
  store: AdminStore,
  previewUserId: number | null,
): Access {
  const isSuperadmin = user?.role === 'superadmin';

  const previewed = isSuperadmin && previewUserId != null
    ? store.users.find((u) => u.id === previewUserId) ?? null
    : null;

  if (isSuperadmin && !previewed) {
    return {
      kind: 'superadmin',
      preview: false,
      roleName: 'Superadmin',
      subject: null,
      role: null,
      companyId: null,
      can: () => true,
      canOpen: () => true,
      seesWell: () => true,
      layout: layoutOf(store, STANDARD_LAYOUT_ID),
      admin: { structure: true, users: true },
    };
  }

  // With a real API the signed-in person's own role, wells and layout come
  // with their profile, so they are known before — and whether or not — the
  // admin area's lists can be read. Most people may read none of them.
  const mine = !previewed ? fromProfile(user, store) : null;

  const email = user?.email.trim().toLowerCase();
  const subject = previewed ?? mine?.subject ?? store.users.find((u) => u.email === email) ?? null;

  if (!subject) {
    return {
      kind: 'legacy',
      preview: false,
      roleName: user?.role ?? '',
      subject: null,
      role: null,
      companyId: null,
      can: (permission) => {
        const meta = PERMISSIONS.find((p) => p.key === permission);
        return meta && 'menu' in meta ? mayOpen(user, meta.menu) : false;
      },
      canOpen: (menu) => mayOpen(user, menu),
      seesWell: () => true,
      layout: layoutOf(store, STANDARD_LAYOUT_ID),
      admin: { structure: false, users: false },
    };
  }

  const role = mine?.role ?? store.roles.find((r) => r.id === subject.roleId) ?? null;
  const company = store.companies.find((c) => c.id === subject.companyId);

  // Switched off, or left without a role or a company: known, and allowed
  // nothing. A profile from the API is live by definition: the server does not
  // sign in an account, or a company, that is switched off.
  const live = mine ? true : Boolean(role && company?.active && subject.active);
  const held = new Set<PermissionKey>(live && role ? role.permissions : []);
  const scope: WellScope = live && role ? role.scope : 'none';

  return {
    kind: 'role',
    preview: Boolean(previewed),
    roleName: role?.name ?? 'No role',
    subject,
    role,
    companyId: subject.companyId,
    can: (permission) => held.has(permission),
    canOpen: (menu) => held.has(MENU_PERMISSION[menu]),
    seesWell: (well) => {
      if (scope === 'none' || well.companyId !== subject.companyId) return false;
      return scope === 'all' || subject.wellIds.includes(well.id);
    },
    layout: mine?.layout ?? layoutOf(store, role?.layoutId ?? STANDARD_LAYOUT_ID),
    admin: {
      structure: false,
      users: held.has('users.manage') || held.has('welladmins.manage'),
    },
  };
}

/**
 * The signed-in person as the API describes them, when they hold a defined
 * role: null for a fixed role, for the mock, and for nobody.
 */
function fromProfile(
  user: User | null,
  store: AdminStore,
): { subject: AdminUser; role: Role; layout: NavLayout | null } | null {
  if (!user || user.roleId == null || user.companyId == null) return null;

  const scope = (['none', 'one', 'many', 'all'] as const).find((s) => s === user.scope) ?? 'none';
  const role: Role = store.roles.find((r) => r.id === user.roleId) ?? {
    id: user.roleId,
    name: user.role,
    description: '',
    scope,
    permissions: (user.permissions ?? []).filter(isPermissionKey),
    layoutId: user.layout?.id ?? STANDARD_LAYOUT_ID,
  };

  return {
    subject: {
      id: Number(user.id),
      name: user.name,
      email: user.email.trim().toLowerCase(),
      companyId: user.companyId,
      roleId: user.roleId,
      wellIds: user.wellIds ?? [],
      active: true,
      createdAt: user.createdAt ?? '',
      createdBy: '',
    },
    // What the server says now wins over a list that may be a moment old.
    role: { ...role, scope, permissions: (user.permissions ?? role.permissions).filter(isPermissionKey) },
    layout: user.layout ? sanitiseLayout(user.layout) : null,
  };
}

// --------------------------------------------------------- managing people

const isWellAdminRole = (role: Role) =>
  role.permissions.includes('users.manage') && !role.permissions.includes('welladmins.manage');

const isMemberRole = (role: Role) =>
  !role.permissions.includes('users.manage') && !role.permissions.includes('welladmins.manage');

/**
 * The roles this person may hand out. The chain is one step at a time: the
 * superadmin creates anyone; whoever holds "create Well Admins" creates Well
 * Admins; whoever holds "manage users" creates the people who do the work.
 * Nobody creates their own equal.
 */
export function assignableRoles(access: Access, roles: Role[]): Role[] {
  if (access.kind === 'superadmin') return roles;
  return roles.filter(
    (role) =>
      (access.can('welladmins.manage') && isWellAdminRole(role)) ||
      (access.can('users.manage') && isMemberRole(role)),
  );
}

/** The wells this person may put a user on, within one company. */
export function assignableWells(access: Access, wells: AdminWell[], companyId: number): AdminWell[] {
  const inCompany = wells.filter((w) => w.companyId === companyId);
  if (access.kind === 'superadmin' || access.can('welladmins.manage')) return inCompany;
  return inCompany.filter(access.seesWell);
}

/** The accounts this person can see in the Users list. */
export function visibleUsers(access: Access, users: AdminUser[]): AdminUser[] {
  if (access.kind === 'superadmin') return users;
  if (!access.subject || !access.admin.users) return [];

  const me = access.subject;
  const sameCompany = users.filter((u) => u.companyId === me.companyId);
  if (access.can('welladmins.manage')) return sameCompany;

  // A Well Admin sees the people on their wells, and themselves.
  return sameCompany.filter(
    (u) => u.id === me.id || u.wellIds.some((id) => me.wellIds.includes(id)) || u.createdBy === me.email,
  );
}

/** Whether this person may edit that account. Seeing it is not enough. */
export function mayManageUser(access: Access, target: AdminUser, store: AdminStore, wells: AdminWell[]): boolean {
  if (access.kind === 'superadmin') return true;
  if (!access.subject || target.id === access.subject.id) return false;
  if (target.companyId !== access.subject.companyId) return false;

  const role = store.roles.find((r) => r.id === target.roleId);
  if (!role || !assignableRoles(access, store.roles).some((r) => r.id === role.id)) return false;

  if (access.can('welladmins.manage')) return true;

  const mine = new Set(assignableWells(access, wells, target.companyId).map((w) => w.id));
  return target.wellIds.every((id) => mine.has(id));
}

// --------------------------------------------------------------- validation

export function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

/** What is wrong with a user's wells for the role they were given, or null. */
export function wellsProblem(role: Role, wellIds: number[]): string | null {
  if (role.scope === 'one' && wellIds.length !== 1) return `${role.name} is given exactly one well.`;
  if (role.scope === 'many' && wellIds.length === 0) return `${role.name} needs at least one well.`;
  return null;
}

/** "3 wells", "Tapis Deep-1", "All wells", "—": a user's reach in a few words. */
export function describeReach(user: AdminUser, role: Role | undefined, wells: AdminWell[]): string {
  if (!role || role.scope === 'none') return 'No wells';
  if (role.scope === 'all') return 'All wells of the company';

  const names = user.wellIds.map((id) => wells.find((w) => w.id === id)?.name ?? `Well ${id}`);
  if (names.length === 0) return '—';
  return names.length <= 2 ? names.join(', ') : `${names.length} wells`;
}
